/**
 * Vertical Accordion Component (Semantic WAI-ARIA Pattern)
 *
 * - Uses <button> inside heading for accessible toggle.
 * - Only one panel open at a time (data-single-open).
 * - Supports closing the currently open panel (all can be closed).
 * - Smooth height animations with inline styles.
 */

import { setAriaExpanded } from '../Utils/accessibility-utils';

class VerticalAccordion {
    private element: HTMLElement;
    private buttons: NodeListOf<HTMLButtonElement>;
    private currentlyOpenButton: HTMLButtonElement | null = null;

    constructor(element: HTMLElement) {
        this.element = element;
        this.buttons = element.querySelectorAll<HTMLButtonElement>('.accordion-item__header');
        this.init();
    }

    private init(): void {
        // Open first panel by default (optional)
        const firstButton = this.buttons[0];
        if (firstButton) {
            this.openPanel(firstButton, false);
        }

        this.buttons.forEach(button => {
            button.addEventListener('click', () => this.onButtonClick(button));
        });
    }

    private onButtonClick(button: HTMLButtonElement): void {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';

        if (!isExpanded) {
            // Open selected
            this.openPanel(button, true);
        } else {
            // Close if already open
            this.closePanel(button, true);
        }
    }

    private getContentElement(button: HTMLButtonElement): HTMLElement | null {
        const contentId = button.getAttribute('aria-controls');
        if (!contentId) return null;
        return this.element.querySelector<HTMLElement>('#' + contentId);
    }

    private openPanel(button: HTMLButtonElement, animate: boolean): void {
        const content = this.getContentElement(button);
        if (!content) return;

        // If single-open behavior, close any other open panel
        if (this.element.hasAttribute('data-single-open')) {
            this.buttons.forEach(other => {
                if (other !== button && other.getAttribute('aria-expanded') === 'true') {
                    this.closePanel(other, animate);
                }
            });
        }

        setAriaExpanded(button, true);
        this.currentlyOpenButton = button;

        content.hidden = false;
        content.classList.add('is-open');

        // Height animation
        if (animate) {
            content.style.height = '0px';
            // force reflow
            content.offsetHeight;
        }

        const targetHeight = content.scrollHeight;
        content.style.height = targetHeight + 'px';

        const onEnd = (e: TransitionEvent) => {
            if (e.propertyName === 'height') {
                content.style.height = 'auto'; // allow dynamic height afterwards
                content.removeEventListener('transitionend', onEnd);
            }
        };
        content.addEventListener('transitionend', onEnd);
    }

    private closePanel(button: HTMLButtonElement, animate: boolean): void {
        const content = this.getContentElement(button);
        if (!content) return;

        setAriaExpanded(button, false);
        if (this.currentlyOpenButton === button) {
            this.currentlyOpenButton = null;
        }

        // Prepare for collapse animation
        content.style.height = content.scrollHeight + 'px';
        // force reflow
        content.offsetHeight;

        if (animate) {
            content.style.height = '0px';
        } else {
            content.style.height = '0px';
        }

        const onEnd = (e: TransitionEvent) => {
            if (e.propertyName === 'height') {
                content.hidden = true;
                content.classList.remove('is-open');
                content.removeEventListener('transitionend', onEnd);
            }
        };
        content.addEventListener('transitionend', onEnd);
    }
}

// Initialize all vertical accordions when the DOM is fully loaded
document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll<HTMLElement>('.vertical-accordion[data-single-open]').forEach(acc => {
        new VerticalAccordion(acc);
    });
});